import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, retry: () => void) => ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface in console for debugging; harness suppresses noisy traces in tests.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  retry = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    const { error } = this.state;
    const { children, fallback } = this.props;
    if (error == null) return children;
    if (fallback) return fallback(error, this.retry);
    return (
      <div data-testid="error-boundary-fallback" className="error-boundary">
        <div className="error-boundary-title">Something went sideways.</div>
        <div data-testid="error-boundary-message" className="error-boundary-message">
          {error.message}
        </div>
        <button
          type="button"
          data-testid="error-boundary-retry"
          className="error-boundary-retry"
          onClick={this.retry}
        >
          Retry
        </button>
      </div>
    );
  }
}
